/**
 * Preview-mode entry gate.
 */
import React, { useEffect } from "react";
import { Box } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";

import { VText } from "form";
import { useAsterController } from "context";
import { actionAppCourseDetailGet, actionPreviewModeSet } from "context/action";
import { getFirstVerticalRoute } from "utils/course";

/**
 * Enables preview mode and redirects into the first previewable unit.
 */
const BoardPreviewGate = () => {

  const { t } = useTranslation('common');

  const { courseId, blockId } = useParams();
  const navigate = useNavigate();

  const [controller, dispatch] = useAsterController();
  const { structure, loadedDashboard, loadedCourseDetail, user } = controller;

  useEffect(() => courseId && structure.course_id !== courseId && actionAppCourseDetailGet(dispatch, courseId), [courseId, dispatch, structure.course_id]);

  useEffect(() => {
    if (loadedDashboard && loadedCourseDetail && user.permission !== 'USER') {
      actionPreviewModeSet(dispatch, courseId);

      let link = getFirstVerticalRoute(structure);
      if (blockId) {
        for (let i = 0; i < structure.sections?.length; i++) {
          const section = structure.sections[i];
          if (!section.sequences) continue;
          for (let j = 0; j < section.sequences?.length; j++) {
            const sequence = section.sequences[j];
            if (!sequence.verticals) continue;
            for (let k = 0; k < sequence.verticals?.length; k++) {
              if (sequence.verticals[k]?.vertical_id === blockId) {
                link = `${i}/${j}/${k}`;
              }
            }
          }
        }
      }

      // Empty Studio courses have no units — fall back to the course overview
      // instead of /preview/.../0/0/0 which spins skeletons forever.
      if (!link) {
        navigate(`/course/${courseId}`, { replace: true });
        return;
      }
      navigate(`/preview/${courseId}/${link}`, { replace: true });
    }
  }, [user.permission, structure, loadedDashboard, loadedCourseDetail, blockId, courseId, dispatch, navigate]);

  return <>
    <Box className="m-0 md:m-6 v-bg-body rounded-none md:rounded-md flex-1 flex justify-center items-center">
      {
        loadedDashboard ?
          <>
            {!['SUPERADMIN', 'STAFF', 'ADMIN'].includes(user.permission) && <VText className="text-2xl">{t("You do not have permission to preview this course!")}</VText>}
          </>
          :
          <Skeleton containerClassName="w-8/12" className="h-10 mt-3 w-full" count={5} />
      }
    </Box>
  </>;
};

export default BoardPreviewGate;